import { Trans } from "@lingui/macro";
import * as Popover from "@radix-ui/react-popover";
import { PaintBrush, PresentationChart } from "phosphor-react";
import { useEffect, useState } from "react";
import { create } from "zustand";

import { diagramTools } from "../lib/graphOptions";
import { Button2 } from "../ui/Shared";
import { useCanEdit } from "../lib/hooks";
import { useDiagramTypeTool } from "../lib/useDiagramTool";
import { useDoc } from "../lib/useDoc";
import produce from "immer";
import { EditorActionTextButton } from "../ui/EditorActionTextButton";

export const useDiagramTool = create<{ diagramTool?: string }>((_set) => ({}));

export function LanguageDropdown() {
  const [preview, setPreview] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const canEdit = useCanEdit();
  const [selectedDiagramTool] = useDiagramTypeTool();

  useEffect(() => {
    if (typeof selectedDiagramTool === "string" || !selectedDiagramTool) {
      setPreview(selectedDiagramTool as "string");
    }
  }, [selectedDiagramTool]);

  return (
    <Popover.Root
      modal
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
      }}
    >
      <Popover.Trigger
        // className="text-xs font-medium p-3 pr-5 pl-4 rounded-md bg-neutral-200 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-300 dark:hover:bg-neutral-700 flex active:opacity-90 items-center space-x-1"
        disabled={!canEdit}
      >
        <EditorActionTextButton icon={PresentationChart}>
          <Trans>Diagram Type: </Trans>
          
          <b className="text-neutral-700 dark:text-neutral-300">
            {selectedDiagramTool}
          </b>
        </EditorActionTextButton>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
          className="rounded p-2 pt-4 w-[260px] bg-neutral-100 shadow-md will-change-[transform,opacity] data-[state=open]:data-[side=top]:animate-slideDownAndFade data-[state=open]:data-[side=right]:animate-slideLeftAndFade data-[state=open]:data-[side=bottom]:animate-slideUpAndFade data-[state=open]:data-[side=left]:animate-slideRightAndFade max-h-[calc(100vh_-_340px)] grid grid-rows-[auto_minmax(0,1fr)_auto] dark:bg-neutral-900"
          sideOffset={10}
          align="end"
        >
          <div className="grid gap-1 mb-2 px-1">
            <h3 className="text-base font-bold">
              <Trans>Choose diagram type</Trans>
            </h3>
          </div>
          <div className="grid overflow-auto">
            {diagramTools.map((tool) => (
              <button
                key={tool.value}
                className="text-left text-xs p-2 rounded w-full hover:bg-neutral-300 active:bg-neutral-300 dark:hover:bg-neutral-800 dark:active:bg-neutral-800 focus:bg-neutral-200 dark:focus:bg-neutral-800 focus:outline-none data-[active=true]:bg-neutral-400 dark:data-[active=true]:bg-neutral-700"
                onClick={() => {
                  setPreview(tool.value);
                }}
                data-active={preview === tool.value}
              >
                {tool.label()}
              </button>
            ))}
          </div>
          <p className="px-1 text-[12px] text-neutral-700 dark:text-neutral-200 my-2 leading-normal">
            <Trans>
              Please check the preview of a diagram tool before you choose it to
              ensure that it is compatible with your current syntax.
            </Trans>
          </p>
          <Button2
            className="mt-2 w-full"
            disabled={!preview}
            color="blue"
            onClick={() => {
              setIsOpen(false);
              setPreview("");
              useDiagramTool.setState({
                diagramTool: preview,
              });
              if (preview) {
                useDoc.setState(
                  (s) => {
                    return produce(s, (draft) => {
                      draft.meta.diagramTool = preview;
                    });
                  },
                  false,
                  "Graph/DiagramTool"
                );
              }
            }}
          >
            <Trans>Pick this tool</Trans>
          </Button2>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
